import { useInfiniteQuery, useAuthStore } from '@northladder/services';

import { CUSTOMER_SERVICE_API } from '../customerServiceClient';
import { IFetchAppraisalsPayload } from '../types';

/**
 * This is to track the appraisals listing key in react query cache.
 */
export const FETCH_APPRAISALS_KEY = 'fetch-appraisals-key';

/**
 * This performs a get request and retrieves the appraisals list data by
 * category.
 * Provides data fetching states to be used in the appraisals table.
 */
export const useAppraisalsQuery = (payload: IFetchAppraisalsPayload) => {
  const {
    brandId,
    categoryId,
    count,
    dealProcessType,
    fromDate,
    listType,
    search,
    toDate,
    userId: idUser,
  } = payload;
  const userId = idUser || useAuthStore.getState()?.activeSession?.userId || '';

  const localPayload = {
    dealProcessType,
    count,
    listType,
    userId,
    // If the categoryId or brandiId is 'all', we don't need to pass it in the payload.
    ...(brandId && brandId !== 'all' ? { brandId } : {}),
    ...(categoryId && categoryId !== 'all' ? { categoryId } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(search ? { search } : {}),
    ...(toDate ? { toDate } : {}),
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPending,
    isPaused,
    refetch,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      FETCH_APPRAISALS_KEY,
      brandId,
      categoryId,
      dealProcessType,
      fromDate,
      listType,
      search,
      toDate,
      userId,
    ],
    enabled: false, // It's a dependant query so don't run immediately.
    networkMode: 'always', // Even make calls when offline
    queryFn: ({ pageParam = 1 }) =>
      CUSTOMER_SERVICE_API.fetchAppraisals({
        ...localPayload,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentAssetCount = allPages.length * payload.count;

      return currentAssetCount < lastPage.count ? nextPage : undefined;
    },
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isPaused,
    isFetchingNextPage,
    refetch,
  };
};
